import { useMutation, useQueryClient } from '@tanstack/react-query';
import cn from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { TOTP } from '#api';
import {
  AuthenticationError,
  handleServerFormError,
  InternalServerError,
  trackError,
  ValidationError,
} from '#api/errors.ts';
import { totpAuthenticatorsQueryOptions } from '#api/totp-authenticators.queries.ts';
import { Divider } from '#components/atoms/divider/Divider.tsx';
import { ErrorMessage, Input, MFAInput } from '#components/atoms/forms/index.ts';
import { QRCode } from '#components/atoms/qr/QRCode.tsx';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { useAuth } from '#global/context/auth/useAuth.tsx';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { useMFAInput } from '#hooks/useMFAInput.ts';
import { useMergedRefs } from '#util/mergeRefs.ts';
import { chunkStringIntoFours } from '../chunkStringIntoFours.ts';
import styles from './AddTOTPForm.module.css';

const MFA_LENGTH_ERROR = 'The code must be 6 digits long.';

type FormData = {
  code: string;
  name: string;
};

type Authenticator = {
  totpToken: string;
  url: string;
  secret: string;
};

type Props = {
  onSuccess: () => void;
  onError?: () => void;
  allowCustomName?: boolean;
  smallQR?: boolean;
};

export function AddTOTPForm({ onSuccess, onError, allowCustomName, smallQR }: Props) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useAppLocation();
  const [authenticator, setAuthenticator] = useState<Authenticator | undefined>();
  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty, isSubmitting, isSubmitted, isSubmitSuccessful },
    setValue,
    setError,
    setFocus,
  } = useForm<FormData>({ mode: 'onChange', defaultValues: { code: '', name: allowCustomName ? '' : 'Primary' } });
  const { state, inputRef } = useMFAInput<'code'>({
    isSubmitting,
    isSubmitted,
    isSubmitSuccessful,
    setFocus,
    setValue,
    name: 'code',
  });
  const auth = useAuth();
  // React 18 fires useEffect twice in dev mode, but we don't want two mutations.  So track whether we've already requested.
  const hasRequestedAuthenticatorRef = useRef(false);
  const chunkedSecret = authenticator?.secret ? chunkStringIntoFours(authenticator.secret) : undefined;
  const registerMFAMutation = useMutation({
    mutationFn: auth.registerMFA,
    onSuccess: () => {
      void queryClient.invalidateQueries(totpAuthenticatorsQueryOptions);
    },
  });
  const requestAuthenticatorMutation = useMutation({
    mutationFn: TOTP.requestMFAAuthenticator,
    onSuccess: (res) => {
      return setAuthenticator(res);
    },
    onError: (err) => {
      if (err instanceof AuthenticationError && err.code === 'ERR_2FA_RENEWAL_REQUIRED') {
        void navigate('/auth/mfa/renew', { state: { from: location }, replace: true });
      } else {
        trackError(err);
        // TODO: What should we do if there is more than one error (unlikely)?
        if (err instanceof ValidationError && err.errors[0] && 'reworded' in err.errors[0] && err.errors[0].reworded) {
          setError('root.global', { message: err.errors[0].message });
        } else {
          setError('root.global', { message: 'Could not create authenticator. Please contact support.' });
        }
        onError?.();
      }
    },
  });
  const submitRef = useRef<HTMLInputElement>(null);

  // Auto-submit
  useEffect(() => {
    if (isDirty && isValid && !isSubmitting && !isSubmitSuccessful) {
      submitRef.current?.click();
    }
  }, [isValid, isDirty, isSubmitting, isSubmitSuccessful]);

  // Destructuring since the mutator object is not referentially stable.
  const { isIdle: requestAuthenticatorMutationIsIdle, mutate: requestAuthenticatorMutationMutate } =
    requestAuthenticatorMutation;
  useEffect(() => {
    if (requestAuthenticatorMutationIsIdle && !hasRequestedAuthenticatorRef.current) {
      hasRequestedAuthenticatorRef.current = true;
      requestAuthenticatorMutationMutate();
    }
  }, [requestAuthenticatorMutationIsIdle, requestAuthenticatorMutationMutate]);

  /** Request a new authenticator and clear out the existing code */
  function resetForm() {
    setAuthenticator(undefined);
    requestAuthenticatorMutation.mutate();
    setValue('code', '');
    setFocus('code');
  }

  const onSubmit = handleSubmit(async (data) => {
    // This shouldn't be possible, because the input is disabled until we have an authenticator,
    // and the form can't be submitted without a value in the input.
    if (!authenticator) {
      trackError(new Error('No authenticator when submitting form.'));
      return;
    }

    // Avoid double-submission (e.g. autosubmit + enter press)
    if (isSubmitting || isSubmitSuccessful) {
      return;
    }

    try {
      await registerMFAMutation.mutateAsync({ totpToken: authenticator.totpToken, code: data.code, name: data.name });

      onSuccess();
    } catch (error) {
      if (error instanceof ValidationError) {
        for (const err of error.errors) {
          if (err.path === 'code') {
            setValue('code', '');
            setFocus('code');
            handleServerFormError(error, ({ path, message }) => {
              switch (path) {
                case 'code':
                case 'name':
                  setError(path, { message });
                  return true;
                case undefined:
                  setError('root.global', { message });
                  return true;
                default:
                  setError('root.global', { message });
                  return false;
              }
            });
          } else if (err.code === 'ERR_INVALID_TOTP_TOKEN') {
            resetForm();
            setError('code', err);
          }
        }
      } else if (error instanceof InternalServerError) {
        resetForm();
        setError('code', { message: 'There was an issue, please re-scan and try again' });
      }
    }
  });

  const { ref, ...inputProps } = register('code', {
    required: 'Enter the 6 digit code from your app.',
    pattern: { value: /[0-9]{6}/, message: 'The 6 digit code is numbers 0-9' },
    minLength: { value: 6, message: MFA_LENGTH_ERROR },
    maxLength: { value: 6, message: MFA_LENGTH_ERROR },
  });

  const mergedRef = useMergedRefs(ref, inputRef);

  return (
    <form id="register-mfa" className="simple-page-content" method="post" onSubmit={onSubmit}>
      {errors.root?.global?.message ?
        <ErrorMessage>{errors.root.global.message}</ErrorMessage>
      : <>
          {allowCustomName ?
            <Field id="name" label="Name" className="mb-24">
              <Input
                className="width-100"
                {...register('name', {
                  maxLength: { value: 50, message: 'TOTP name cannot be longer than 50 characters' },
                })}
              />
            </Field>
          : null}

          <p className="label mb-16">Scan the QR code with your authenticator app</p>
          <QRCode
            className={cn('mb-16', styles.AddTOTPForm_qrCode)}
            value={authenticator?.url}
            title="QR code containing your authenticator setup key"
            small={smallQR}
          />
          {chunkedSecret && (
            <div className={cn('mb-16')}>
              <p className="label">Or copy this code into your authenticator app</p>
              <p className={cn(styles.AddTOTPForm_secretText, 'totpCode')} data-testid="secret-text">
                {chunkedSecret}
              </p>
              {allowCustomName ? null : <Divider />}
            </div>
          )}
          <Field id="code" label="Enter your verification code" className={allowCustomName ? 'mb-24' : 'mb-32'}>
            <MFAInput state={state} disabled={!authenticator} {...inputProps} ref={mergedRef} />

            {errors.code && isSubmitted && errors.code.message !== MFA_LENGTH_ERROR ?
              <ErrorMessage className="mt-8">{errors.code.message}</ErrorMessage>
            : null}
          </Field>
          {allowCustomName ? null : <input ref={submitRef} type="submit" hidden={true} />}
        </>
      }
    </form>
  );
}
